import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import { fetchData } from './api';

type AsyncStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

interface AchievementsState {
  data: unknown[];
  status: AsyncStatus;
  error: string | null;
}

const initialState: AchievementsState = {
  data: [],
  status: 'idle',
  error: null,
};

export const fetchAchievements = createAsyncThunk<unknown[], void, { state: RootState }>(
  'achievements/fetch',
  async (_, { rejectWithValue }) => {
    try {
      const raw = await fetchData({ endpoint: 'achievements' });
      if (Array.isArray(raw)) return raw;
      return [];
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to fetch achievements');
    }
  },
  {
    condition: (_, { getState }) => getState().achievements.status !== 'loading',
  }
);

const achievementsSlice = createSlice({
  name: 'achievements',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAchievements.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchAchievements.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.data = action.payload;
      })
      .addCase(fetchAchievements.rejected, (state, action) => {
        state.status = 'failed';
        state.error = (action.payload as string) ?? action.error.message ?? null;
      });
  },
});

export default achievementsSlice.reducer;
